import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import {
  Dumbbell,
  Flame,
  Sparkles,
  HeartPulse,
  ShieldCheck,
  Clock,
  Lock,
  Target,
  Phone,
  Mail,
  MessageCircle,
  ArrowRight,
  ChevronRight,
  Check,
  Quote,
  MapPin,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE = "07386710866";
const PHONE_INTL = "447386710866"; // UK +44, drop leading 0
const EMAIL = "mateobrysonpt@gmail.com";
const WHATSAPP_TEXT = encodeURIComponent(
  "Hi Mateo, I'd like to enquire about private mobile personal training in Chelsea."
);

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_e50a4bf5-1bdf-41a0-aee8-165d65d803a2/artifacts/it2ev96t_IMG_4773.jpeg";

const SERVICE_IMGS = {
  strength: "https://images.pexels.com/photos/13863727/pexels-photo-13863727.jpeg",
  home: "https://images.pexels.com/photos/13722886/pexels-photo-13722886.jpeg",
  gym: "https://images.pexels.com/photos/29392546/pexels-photo-29392546.jpeg",
};

// ------------- NAV -------------
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#services", label: "Services" },
    { href: "#how", label: "Method" },
    { href: "#about", label: "About" },
    { href: "#pricing", label: "Pricing" },
    { href: "#faq", label: "FAQ" },
  ];

  return (
    <nav
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/75 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="h-8 w-8 border border-[#D4AF37]/60 flex items-center justify-center text-[#D4AF37] font-serif text-sm tracking-widest">
            M
          </span>
          <span className="font-serif text-base tracking-widest text-[#f4f4f0] group-hover:text-[#D4AF37] transition-colors">
            MATEO&nbsp;BRYSON
          </span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-[13px] tracking-[0.18em] uppercase text-[#a1a1aa] hover:text-[#D4AF37] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>

        <a
          href="#contact"
          data-testid="nav-cta-enquire"
          className="group hidden sm:inline-flex items-center gap-2 border border-[#D4AF37]/70 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-5 py-2.5 text-[12px] tracking-[0.22em] uppercase"
        >
          Enquire
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </a>
      </div>
    </nav>
  );
};

// ------------- HERO -------------
const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden grain"
    >
      {/* Image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Mateo Bryson, private personal trainer in Chelsea"
          className="h-full w-full object-cover object-[60%_center] sm:object-[70%_center]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-24 min-h-[100svh] flex flex-col justify-center">
        <div className="max-w-2xl">
          <p className="overline reveal" data-testid="hero-overline">
            Private Personal Trainer · London
          </p>
          <h1
            data-testid="hero-headline"
            className="headline text-white text-[44px] sm:text-6xl lg:text-[76px] mt-6 reveal reveal-delay-1"
          >
            Private Personal Training{" "}
            <span className="italic text-[#D4AF37]">in London.</span>
          </h1>
          <h2
            data-testid="hero-subheadline"
            className="headline text-white/85 text-2xl sm:text-3xl lg:text-4xl mt-6 reveal reveal-delay-2"
          >
            Strength, sculpted{" "}
            <span className="italic text-[#D4AF37]">privately</span>{" "}
            in your own home.
          </h2>
          <p
            data-testid="hero-subcopy"
            className="mt-8 text-[#d4d4d0] text-base sm:text-lg max-w-2xl leading-relaxed reveal reveal-delay-2"
          >
            Bespoke one-to-one coaching for private clients across
            Knightsbridge, Chelsea, Kensington, Belgravia, Mayfair, Fulham,
            Hammersmith and Notting Hill — delivered in the privacy of your
            own space.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal reveal-delay-3">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              onClick={() => trackEvent("cta_book_consultation_click", { location: "hero" })}
              className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#f0e6d2] transition-colors"
            >
              Book a Consultation
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={`tel:${PHONE}`}
              data-testid="hero-cta-call"
              onClick={() => trackEvent("call_button_click", { location: "hero" })}
              className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <Phone className="h-4 w-4" />
              Call 07386 710 866
            </a>
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-6 text-[11px] tracking-[0.2em] uppercase text-[#a1a1aa] reveal reveal-delay-4">
            <span className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-[#D4AF37]" /> 10 Yrs Experience
            </span>
            <span className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-[#D4AF37]" /> REPs L3 &amp; L4
            </span>
            <span className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-[#D4AF37]" /> Rehab Certified
            </span>
          </div>
        </div>
      </div>

      {/* subtle bottom label */}
      <div className="absolute bottom-8 right-8 z-10 hidden lg:flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-white/50">
        <MapPin className="h-3 w-3" />
        Serving Central &amp; West London
      </div>
    </section>
  );
};

// ------------- MARQUEE / TRUST -------------
const Marquee = () => {
  const items = [
    "Strength",
    "Fat Loss",
    "Toning",
    "Rehabilitation",
    "Private",
    "Home Visits",
    "Chelsea",
    "1-to-1",
  ];
  return (
    <div
      data-testid="trust-marquee"
      className="border-y border-white/5 bg-[#0a0a0a] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-center gap-x-12 gap-y-3">
        {items.map((i, idx) => (
          <React.Fragment key={i}>
            <span className="font-serif italic text-[#71717a] text-sm tracking-wide">
              {i}
            </span>
            {idx < items.length - 1 && (
              <span className="text-[#D4AF37]/40 text-xs">✦</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ------------- SERVICES -------------
const Services = () => {
  const services = [
    {
      icon: Dumbbell,
      title: "Strength",
      desc: "Progressive resistance programming built around your body — lean muscle, postural integrity, and quiet, functional power.",
      img: SERVICE_IMGS.strength,
    },
    {
      icon: Flame,
      title: "Fat Loss",
      desc: "Sustainable body composition work — metabolic training and nutrition structure designed for busy, high-performing lives.",
      img: SERVICE_IMGS.gym,
    },
    {
      icon: Sparkles,
      title: "Toning & Sculpting",
      desc: "Refined, high-precision training to shape, define and elongate — without punishing volume or crude gym conventions.",
      img: SERVICE_IMGS.home,
    },
    {
      icon: HeartPulse,
      title: "Rehabilitation",
      desc: "Clinically-informed rehab for injury recovery, chronic pain and post-surgical return — rebuilt with patience and precision.",
      img: SERVICE_IMGS.gym,
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 sm:py-36 bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-20">
          <div className="lg:col-span-5">
            <p className="overline">01 — Services</p>
            <h2 className="headline text-[#f4f4f0] text-4xl sm:text-5xl lg:text-6xl mt-5">
              Four disciplines.{" "}
              <span className="italic text-[#D4AF37]">One standard.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-[#a1a1aa] text-base sm:text-lg leading-relaxed">
              Every programme is written from scratch. No templates, no
              recycled plans. Your session is designed around your body, your
              history, your goals — and delivered in the room you choose.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-white/5">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s|&/g, "-")}`}
                className="group relative bg-[#0a0a0a] p-10 sm:p-12 overflow-hidden hover:bg-[#111111] transition-colors duration-500"
              >
                <div className="absolute right-0 top-0 w-40 h-40 opacity-10 group-hover:opacity-30 transition-opacity duration-700">
                  <img
                    src={s.img}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
                </div>
                <div className="relative">
                  <Icon
                    className="h-6 w-6 text-[#D4AF37]"
                    strokeWidth={1.25}
                  />
                  <h3 className="font-serif text-[28px] sm:text-[32px] text-[#f4f4f0] mt-6">
                    {s.title}
                  </h3>
                  <div className="divider-gold my-5" />
                  <p className="text-[#a1a1aa] leading-relaxed max-w-md">
                    {s.desc}
                  </p>
                  <a
                    href="#contact"
                    data-testid={`service-cta-${s.title.toLowerCase().replace(/\s|&/g, "-")}`}
                    className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#f4f4f0] hover:text-[#D4AF37] transition-colors"
                  >
                    Discuss this programme
                    <ChevronRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ------------- BENEFITS -------------
const Benefits = () => {
  const items = [
    {
      icon: Clock,
      title: "Convenience",
      text: "No commute. No lockers. No waiting for equipment. Training arrives at your door at the time that suits your diary.",
    },
    {
      icon: Lock,
      title: "Privacy",
      text: "Your home, your terms. Discreet, respectful, and entirely free from the social friction of commercial gyms.",
    },
    {
      icon: ShieldCheck,
      title: "Accountability",
      text: "Structured check-ins, objective progress markers, and a coach who genuinely invests in your outcome.",
    },
    {
      icon: Target,
      title: "Tailored Coaching",
      text: "Programming built around your body, injuries, lifestyle and ambitions — never a generic template.",
    },
  ];

  return (
    <section
      id="benefits"
      data-testid="benefits-section"
      className="relative py-28 sm:py-36 bg-[#111111] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-20">
          <p className="overline">02 — Why clients stay</p>
          <h2 className="headline text-[#f4f4f0] text-4xl sm:text-5xl lg:text-6xl mt-5">
            The quiet advantages{" "}
            <span className="italic text-[#D4AF37]">of training at home.</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-12">
          {items.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={b.title}
                data-testid={`benefit-${i}`}
                className="group"
              >
                <Icon
                  className="h-7 w-7 text-[#D4AF37]"
                  strokeWidth={1.25}
                />
                <h3 className="font-serif text-2xl text-[#f4f4f0] mt-6">
                  {b.title}
                </h3>
                <div className="divider-gold my-4" />
                <p className="text-[#a1a1aa] leading-relaxed text-[15px]">
                  {b.text}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ------------- HOW IT WORKS -------------
const HowItWorks = () => {
  const steps = [
    {
      n: "I",
      title: "Consultation",
      text: "A private conversation to understand your goals, history, and schedule. No pressure, no sales pitch.",
    },
    {
      n: "II",
      title: "Assessment",
      text: "Movement screen, postural analysis and baseline measurements conducted discreetly in your home.",
    },
    {
      n: "III",
      title: "Bespoke Plan",
      text: "A programme written for your body — training, recovery, and nutrition guidance aligned to your life.",
    },
    {
      n: "IV",
      title: "Delivery & Refinement",
      text: "Ongoing sessions, adjustments, and honest progress reviews. The work compounds.",
    },
  ];
  return (
    <section
      id="how"
      data-testid="how-section"
      className="relative py-28 sm:py-36 bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <p className="overline">03 — Method</p>
            <h2 className="headline text-[#f4f4f0] text-4xl sm:text-5xl lg:text-6xl mt-5">
              A measured,{" "}
              <span className="italic text-[#D4AF37]">deliberate</span>{" "}
              process.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-[#a1a1aa] text-base sm:text-lg leading-relaxed">
              No hype. No gimmicks. Just four steps that quietly transform how
              you move, look and feel — over weeks, months, years.
            </p>
          </div>
        </div>

        <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-white/5">
          {steps.map((s, i) => (
            <li
              key={s.n}
              data-testid={`step-${i + 1}`}
              className={`relative p-8 lg:p-10 border-b border-white/5 ${
                i < 3 ? "lg:border-r" : ""
              } md:border-r lg:border-white/5 hover:bg-[#111] transition-colors duration-500`}
            >
              <span className="font-serif italic text-[#D4AF37] text-sm tracking-widest">
                {s.n}
              </span>
              <h3 className="font-serif text-2xl text-[#f4f4f0] mt-4">
                {s.title}
              </h3>
              <p className="text-[#a1a1aa] mt-4 leading-relaxed text-[15px]">
                {s.text}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

// ------------- ABOUT -------------
const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-28 sm:py-36 bg-[#111111] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_london-pt-premium/artifacts/a5yagd28_D7111B99-8149-439E-B052-63211FBEB36A.png"
                alt="Mateo Bryson — private personal trainer, London"
                className="w-full h-full object-cover object-center grayscale-[15%]"
              />
              <div className="absolute inset-0 ring-1 ring-[#D4AF37]/20 pointer-events-none" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline">04 — About</p>
            <h2 className="headline text-[#f4f4f0] text-4xl sm:text-5xl lg:text-6xl mt-5">
              Meet Mateo.
            </h2>
            <div className="divider-gold my-8" />
            <div className="space-y-6 text-[#a1a1aa] leading-relaxed text-base sm:text-lg">
              <p>
                Over the past decade I've worked with executives, creatives,
                new mothers and returning athletes across Chelsea and West
                London. My approach is deliberately quiet — no clichés, no
                camera-ready theatrics, no punishing for its own sake.
              </p>
              <p>
                What I offer is clear: an intelligent, privately-delivered
                programme that respects your time, your body and your history
                — and moves you, patiently, towards the version of yourself
                you actually want to meet.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6 mt-12 pt-10 border-t border-white/5">
              <div>
                <div className="font-serif text-4xl text-[#D4AF37]">10+</div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#71717a] mt-2">
                  Years Coaching
                </div>
              </div>
              <div>
                <div className="font-serif text-4xl text-[#D4AF37]">L3&nbsp;&amp;&nbsp;L4</div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#71717a] mt-2">
                  REPs Qualified
                </div>
              </div>
              <div>
                <div className="font-serif text-4xl text-[#D4AF37]">CERT.</div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#71717a] mt-2">
                  Rehabilitation
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ------------- PRICING -------------
const Pricing = () => {
  const tiers = [
    {
      name: "Single Session",
      price: "£120",
      unit: "/ session",
      desc: "60 minutes of private, 1-to-1 training in your home.",
      features: [
        "Fully bespoke session",
        "Equipment provided",
        "Flexible scheduling",
      ],
      cta: "Book a Session",
      testid: "pricing-single",
    },
    {
      name: "Ten-Session Block",
      price: "£1,100",
      unit: "/ block",
      desc: "Our most-chosen commitment — designed for tangible progress.",
      features: [
        "Save £100",
        "Structured progression",
        "Priority booking windows",
        "Nutrition framework",
      ],
      cta: "Reserve Block",
      featured: true,
      testid: "pricing-ten",
    },
    {
      name: "Twenty-Session Block",
      price: "£2,000",
      unit: "/ block",
      desc: "The long-form transformation — ideal for serious physique change.",
      features: [
        "Save £400",
        "Full programme design",
        "Quarterly review & retest",
        "Priority diary access",
      ],
      cta: "Reserve Block",
      testid: "pricing-twenty",
    },
    {
      name: "Rehab Assessment",
      price: "£150",
      unit: "/ assessment",
      desc: "In-depth movement & injury screen with a written report.",
      features: [
        "90-minute session",
        "Written rehab protocol",
        "Integrates with your clinician",
      ],
      cta: "Book Assessment",
      testid: "pricing-rehab",
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-28 sm:py-36 bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-6">
            <p className="overline">05 — Investment</p>
            <h2 className="headline text-[#f4f4f0] text-4xl sm:text-5xl lg:text-6xl mt-5">
              Indicative pricing.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-[#a1a1aa] text-base leading-relaxed">
              Rates shown are a guide for Chelsea &amp; surrounding postcodes.
              Final proposals are tailored to your schedule, location and the
              programme designed for you.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {tiers.map((t) => (
            <div
              key={t.name}
              data-testid={t.testid}
              className={`relative flex flex-col p-8 border transition-all duration-500 ${
                t.featured
                  ? "bg-[#141414] border-[#D4AF37]/50"
                  : "bg-[#111111] border-white/5 hover:border-[#D4AF37]/30"
              }`}
            >
              {t.featured && (
                <span className="absolute top-4 right-4 text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] border border-[#D4AF37]/50 px-2 py-1">
                  Most Chosen
                </span>
              )}
              <h3 className="font-serif text-2xl text-[#f4f4f0]">{t.name}</h3>
              <div className="divider-gold my-5" />
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-5xl text-[#f4f4f0]">
                  {t.price}
                </span>
                <span className="text-[#71717a] text-sm">{t.unit}</span>
              </div>
              <p className="text-[#a1a1aa] mt-4 text-[15px] leading-relaxed">
                {t.desc}
              </p>
              <ul className="mt-6 space-y-3 text-sm text-[#d4d4d0]">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check
                      className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0"
                      strokeWidth={1.5}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid={`${t.testid}-cta`}
                className={`mt-8 inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.25em] uppercase px-6 py-3.5 transition-colors ${
                  t.featured
                    ? "bg-[#D4AF37] text-black hover:bg-[#f0e6d2]"
                    : "border border-white/20 text-[#f4f4f0] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
              >
                {t.cta}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ------------- TESTIMONIALS -------------
const Testimonials = () => {
  const quotes = [
    {
      text: "Mateo has completely changed the way I think about training. It's intelligent, it's private, and it genuinely fits around my diary. I've never felt stronger.",
      author: "Isabella R.",
      role: "Client · SW3",
    },
    {
      text: "After a difficult post-surgical period, Mateo rebuilt my confidence in my own body. His rehab knowledge is in a different league to anyone I've worked with.",
      author: "Charles H.",
      role: "Client · SW1",
    },
    {
      text: "Discreet, exceptionally professional, and quietly relentless about results. Exactly what I wanted from a personal trainer in Chelsea.",
      author: "Amélie D.",
      role: "Client · W8",
    },
  ];
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-28 sm:py-36 bg-[#111111] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <p className="overline">06 — Clients</p>
          <h2 className="headline text-[#f4f4f0] text-4xl sm:text-5xl lg:text-6xl mt-5">
            In their{" "}
            <span className="italic text-[#D4AF37]">own words.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-white/5">
          {quotes.map((q, i) => (
            <figure
              key={q.author}
              data-testid={`testimonial-${i}`}
              className="bg-[#0a0a0a] p-10 flex flex-col"
            >
              <Quote className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.25} />
              <blockquote className="font-serif text-[22px] leading-[1.5] text-[#f4f4f0] mt-6 flex-1">
                “{q.text}”
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-white/5">
                <div className="text-[#f4f4f0] text-sm">{q.author}</div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#71717a] mt-1">
                  {q.role}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

// ------------- FAQ -------------
const FAQ = () => {
  const faqs = [
    {
      q: "Which areas of London do you cover?",
      a: "Primarily Knightsbridge, Chelsea, Kensington, Belgravia, Mayfair, Fulham, Hammersmith and Notting Hill. Wider Central and West London is possible — please enquire.",
    },
    {
      q: "Do I need equipment or space at home?",
      a: "No. I bring all necessary equipment — bands, kettlebells, TRX, and diagnostic tools. A cleared area roughly the size of a large rug is plenty.",
    },
    {
      q: "How often should we train?",
      a: "Most clients train 2–4 times per week. For serious physique or rehab goals, 3 is the sensible minimum. We'll decide together at consultation.",
    },
    {
      q: "Can you work alongside my physiotherapist or clinician?",
      a: "Absolutely. My rehab certification means I'm comfortable collaborating with physios, osteopaths and consultants to ensure a seamless continuum of care.",
    },
    {
      q: "What does a first session look like?",
      a: "We begin with a thorough consultation and movement screen. You'll leave with immediate clarity on your starting point and a plan to address it.",
    },
    {
      q: "What is your cancellation policy?",
      a: "I ask for 24 hours' notice for cancellations so I can reallocate the slot fairly. Emergencies are always handled with understanding.",
    },
  ];

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-28 sm:py-36 bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline">07 — FAQ</p>
            <h2 className="headline text-[#f4f4f0] text-4xl sm:text-5xl lg:text-6xl mt-5 sticky top-28">
              Answers, <br />
              <span className="italic text-[#D4AF37]">before you ask.</span>
            </h2>
          </div>
          <div className="lg:col-span-8">
            <Accordion
              type="single"
              collapsible
              className="w-full"
              data-testid="faq-accordion"
            >
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-white/10"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="font-serif text-left text-[18px] sm:text-[22px] text-[#f4f4f0] hover:text-[#D4AF37] hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#a1a1aa] text-base leading-relaxed pb-6 max-w-2xl">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

const CALENDLY_URL =
  "https://calendly.com/mateobrysonpt/30min?hide_gdpr_banner=1&hide_landing_page_details=1&primary_color=D4AF37&text_color=f4f4f0&background_color=0a0a0a";

const openCalendly = () => {
  trackEvent("discovery_call_opened", {});
  if (typeof window !== "undefined" && window.Calendly) {
    window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    return false;
  }
  // Fallback: open in a new tab if the widget hasn't loaded yet
  window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
  return false;
};

// ------------- DISCOVERY CALL -------------
const DiscoveryCall = () => {
  return (
    <section
      id="discovery"
      data-testid="discovery-section"
      className="relative py-20 sm:py-24 bg-[#0a0a0a] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div
          className="relative overflow-hidden bg-[#111111] border border-[#D4AF37]/30 p-10 sm:p-16 grid lg:grid-cols-12 gap-10 items-center"
          data-testid="discovery-card"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#D4AF37]/5 blur-3xl pointer-events-none" />

          <div className="lg:col-span-8 relative">
            <p className="overline">Before you commit</p>
            <h2 className="headline text-[#f4f4f0] text-3xl sm:text-4xl lg:text-5xl mt-5">
              Prefer to speak first?{" "}
              <span className="italic text-[#D4AF37]">
                Book a 15-min call.
              </span>
            </h2>
            <p className="mt-6 text-[#a1a1aa] text-base sm:text-lg leading-relaxed max-w-xl">
              A free, private call with Mateo. Fifteen minutes to ask anything
              — how it works, what results look like, whether it&rsquo;s the
              right fit. Zero obligation.
            </p>
          </div>

          <div className="lg:col-span-4 relative flex lg:justify-end">
            <button
              type="button"
              onClick={openCalendly}
              data-testid="discovery-cta"
              className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#f0e6d2] transition-colors"
            >
              Book a Free Call
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    goal: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email so Mateo can reply.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      trackEvent("form_submitted", { goal: form.goal || "unspecified" });
      toast.success(
        "Thank you. Mateo will be in touch personally within 24 hours."
      );
      setForm({ name: "", email: "", phone: "", goal: "", message: "", website: "" });
    } catch (err) {
      toast.error(
        "Something went wrong. Please call or WhatsApp Mateo directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 sm:py-36 bg-[#111111] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="overline">08 — Contact</p>
            <h2 className="headline text-[#f4f4f0] text-4xl sm:text-5xl lg:text-6xl mt-5">
              Begin the{" "}
              <span className="italic text-[#D4AF37]">conversation.</span>
            </h2>
            <div className="divider-gold my-8" />
            <p className="text-[#a1a1aa] leading-relaxed text-base sm:text-lg max-w-md">
              Every engagement begins with a private call. No obligation, no
              sales patter — just a conversation about what you want, and
              whether we're the right fit.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={`tel:${PHONE}`}
                data-testid="contact-phone-link"
                className="group flex items-center gap-5 border-t border-white/10 pt-6"
              >
                <Phone
                  className="h-5 w-5 text-[#D4AF37]"
                  strokeWidth={1.25}
                />
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717a]">
                    Direct line
                  </div>
                  <div className="font-serif text-2xl text-[#f4f4f0] group-hover:text-[#D4AF37] transition-colors mt-1">
                    07386 710 866
                  </div>
                </div>
              </a>
              <a
                href={`https://wa.me/${PHONE_INTL}?text=${WHATSAPP_TEXT}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                onClick={() => trackEvent("whatsapp_click", { location: "contact" })}
                className="group flex items-center gap-5 border-t border-white/10 pt-6"
              >
                <MessageCircle
                  className="h-5 w-5 text-[#D4AF37]"
                  strokeWidth={1.25}
                />
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717a]">
                    WhatsApp
                  </div>
                  <div className="font-serif text-2xl text-[#f4f4f0] group-hover:text-[#D4AF37] transition-colors mt-1">
                    Message Mateo
                  </div>
                </div>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-link"
                className="group flex items-center gap-5 border-t border-b border-white/10 py-6"
              >
                <Mail
                  className="h-5 w-5 text-[#D4AF37]"
                  strokeWidth={1.25}
                />
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717a]">
                    Email
                  </div>
                  <div className="font-serif text-xl sm:text-2xl text-[#f4f4f0] group-hover:text-[#D4AF37] transition-colors mt-1 break-all">
                    mateobrysonpt@gmail.com
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#0a0a0a] border border-white/5 p-8 sm:p-12"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="name"
                    className="text-[10px] tracking-[0.25em] uppercase text-[#71717a]"
                  >
                    Name
                  </Label>
                  <Input
                    id="name"
                    data-testid="form-name"
                    value={form.name}
                    onChange={handle("name")}
                    placeholder="Your full name"
                    className="mt-3 bg-transparent border-0 border-b border-white/15 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-[#f4f4f0] placeholder:text-[#52525b] h-12"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="email"
                    className="text-[10px] tracking-[0.25em] uppercase text-[#71717a]"
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="form-email"
                    value={form.email}
                    onChange={handle("email")}
                    placeholder="you@domain.com"
                    className="mt-3 bg-transparent border-0 border-b border-white/15 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-[#f4f4f0] placeholder:text-[#52525b] h-12"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="phone"
                    className="text-[10px] tracking-[0.25em] uppercase text-[#71717a]"
                  >
                    Phone (optional)
                  </Label>
                  <Input
                    id="phone"
                    data-testid="form-phone"
                    value={form.phone}
                    onChange={handle("phone")}
                    placeholder="07..."
                    className="mt-3 bg-transparent border-0 border-b border-white/15 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-[#f4f4f0] placeholder:text-[#52525b] h-12"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="goal"
                    className="text-[10px] tracking-[0.25em] uppercase text-[#71717a]"
                  >
                    Primary focus
                  </Label>
                  <Input
                    id="goal"
                    data-testid="form-goal"
                    value={form.goal}
                    onChange={handle("goal")}
                    placeholder="Strength · Fat Loss · Toning · Rehab"
                    className="mt-3 bg-transparent border-0 border-b border-white/15 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-[#f4f4f0] placeholder:text-[#52525b] h-12"
                  />
                </div>
              </div>

              <div className="mt-8">
                <Label
                  htmlFor="message"
                  className="text-[10px] tracking-[0.25em] uppercase text-[#71717a]"
                >
                  Tell Mateo a little more
                </Label>
                <Textarea
                  id="message"
                  data-testid="form-message"
                  value={form.message}
                  onChange={handle("message")}
                  rows={5}
                  placeholder="Your goals, any injuries, preferred session times…"
                  className="mt-3 bg-transparent border-0 border-b border-white/15 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-[#f4f4f0] placeholder:text-[#52525b] resize-none"
                />
              </div>

              {/* Honeypot — invisible to humans, irresistible to bots */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-10000px",
                  top: "auto",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
              >
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={handle("website")}
                />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="form-submit"
                  className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#f0e6d2] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Sending…" : "Send Enquiry"}
                  <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href={`https://wa.me/${PHONE_INTL}?text=${WHATSAPP_TEXT}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="form-whatsapp-alt"
                  className="inline-flex items-center justify-center gap-3 border border-white/20 text-[#f4f4f0] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  Or WhatsApp instead
                </a>
              </div>

              <p className="mt-6 text-xs text-[#71717a] leading-relaxed">
                Your details are held privately and used only to respond to
                your enquiry.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ------------- FOOTER -------------
const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="relative bg-[#0a0a0a] border-t border-white/5 py-16"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-3 gap-10 pb-12 border-b border-white/5">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-8 w-8 border border-[#D4AF37]/60 flex items-center justify-center text-[#D4AF37] font-serif text-sm tracking-widest">
                M
              </span>
              <span className="font-serif text-base tracking-widest text-[#f4f4f0]">
                MATEO BRYSON
              </span>
            </div>
            <p className="mt-5 text-sm text-[#a1a1aa] leading-relaxed max-w-xs">
              <a
                href="/private-personal-trainer-london"
                data-testid="footer-link-london"
                className="text-[#a1a1aa] hover:text-[#D4AF37] transition-colors"
              >
                Private mobile personal training
              </a>{" "}
              for Chelsea and West London.
              Strength, fat loss, toning, rehab — delivered privately.
            </p>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717a]">
              Contact
            </div>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a
                  href={`tel:${PHONE}`}
                  data-testid="footer-phone"
                  className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors"
                >
                  07386 710 866
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="footer-email"
                  className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors break-all"
                >
                  mateobrysonpt@gmail.com
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${PHONE_INTL}?text=${WHATSAPP_TEXT}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors"
                >
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717a]">
              Service Area
            </div>
            <ul className="mt-5 space-y-3 text-sm text-[#a1a1aa]">
              <li>Knightsbridge</li>
              <li>Chelsea</li>
              <li>Kensington</li>
              <li>Belgravia</li>
              <li>Mayfair</li>
              <li>Fulham</li>
              <li>Hammersmith</li>
              <li>Notting Hill</li>
            </ul>
          </div>
        </div>
        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-[11px] tracking-[0.2em] uppercase text-[#71717a]">
          <div>© {new Date().getFullYear()} Mateo Bryson · All rights reserved</div>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="hover:text-[#D4AF37] transition-colors"
            >
              Privacy
            </Link>
            <span>Private Mobile PT · London</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ------------- HOMEPAGE SEO (self-asserted on mount) -------------
// Ensures a client-side return trip to '/' from any other route
// re-asserts the homepage's title, description, canonical and og:*.
// Mirrors the exact values in public/index.html so there's no visible
// change on hard load — only on SPA back-navigation.
const HOME_TITLE = "Private Personal Trainer London | Mateo Bryson";
const HOME_DESCRIPTION =
  "Private personal training in London with Mateo Bryson. Bespoke one-to-one coaching for strength, movement, performance and long-term health.";
const HOME_CANONICAL = "https://mateobrysonpt.com/";

const useHomeSeoHead = () => {
  useEffect(() => {
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        if (selector.startsWith("link")) {
          el.setAttribute("rel", "canonical");
        } else if (selector.includes("property=")) {
          el.setAttribute("property", selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes("name=")) {
          el.setAttribute("name", selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    document.title = HOME_TITLE;
    setMeta('meta[name="description"]', "content", HOME_DESCRIPTION);
    setMeta('link[rel="canonical"]', "href", HOME_CANONICAL);
    setMeta('meta[name="robots"]', "content", "index,follow,max-image-preview:large");
    setMeta('meta[property="og:title"]', "content", HOME_TITLE);
    setMeta('meta[property="og:description"]', "content", HOME_DESCRIPTION);
    setMeta('meta[property="og:url"]', "content", HOME_CANONICAL);
  }, []);
};

// ------------- PAGE -------------
export default function Landing() {
  useHomeSeoHead();
  return (
    <div className="relative bg-[#0a0a0a] text-[#f4f4f0] font-sans overflow-x-hidden">
      <Nav />
      <Hero />
      <Marquee />
      <Services />
      <Benefits />
      <HowItWorks />
      <About />
      <Pricing />
      <Testimonials />
      <FAQ />
      <DiscoveryCall />
      <Contact />
      <Footer />
    </div>
  );
}
